import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiX, FiUsers, FiList, FiUser, FiMail } from 'react-icons/fi';

/**
 * Recipient picker for the Compose view.
 *
 * Supports three sources at once (they're combined + deduped server-side):
 *   - Ad-hoc typed-in emails (chips input)
 *   - One or more saved lists
 *   - One or more segments (all team admins / active users / stalled / by team)
 *
 * Emits onChange({ extraEmails, listIds, segments }) whenever anything
 * changes. Also shows a live recipient count via /api/admin/emails/segment
 * previews combined with list sizes.
 *
 * Props:
 *   value        — controlled: { extraEmails, listIds, segments }
 *   onChange     — (value) => void
 *   initialEmails — optional list of emails to seed into extraEmails (used
 *                   by the stalled-signup handoff)
 */
const RecipientPicker = ({ value, onChange, initialEmails }) => {
  const [lists, setLists] = useState([]);
  const [teams, setTeams] = useState([]);
  const [emailInput, setEmailInput] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewCount, setPreviewCount] = useState(0);
  const [previewSample, setPreviewSample] = useState([]);

  // Load data for the pickers
  useEffect(() => {
    axios.get('/api/admin/emails/lists').then(r => setLists(r.data.lists || [])).catch(() => {});
    axios.get('/api/admin/teams').then(r => setTeams(r.data.teams || r.data || [])).catch(() => {});
  }, []);

  // Seed initial emails from props (one-shot)
  useEffect(() => {
    if (initialEmails && initialEmails.length > 0) {
      const merged = Array.from(new Set([...(value.extraEmails || []), ...initialEmails]));
      if (merged.length !== value.extraEmails.length) {
        onChange({ ...value, extraEmails: merged });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recalculate preview whenever selections change
  useEffect(() => {
    (async () => {
      setLoadingPreview(true);
      try {
        const emailSet = new Set(value.extraEmails.map(e => e.toLowerCase()));
        const sample = [...value.extraEmails];

        // Pull list members
        for (const listId of value.listIds) {
          try {
            const r = await axios.get(`/api/admin/emails/lists/${listId}/members`);
            (r.data.contacts || []).forEach(c => {
              if (!c.unsubscribedAt && !c.unsubscribed_at) {
                emailSet.add(c.email.toLowerCase());
                if (sample.length < 5) sample.push(c.email);
              }
            });
          } catch (_) {}
        }

        // Pull segments
        for (const s of value.segments) {
          try {
            const r = await axios.post('/api/admin/emails/segment', s);
            (r.data.recipients || []).forEach(rec => {
              emailSet.add(rec.email.toLowerCase());
              if (sample.length < 5) sample.push(rec.email);
            });
          } catch (_) {}
        }

        setPreviewCount(emailSet.size);
        setPreviewSample(sample.slice(0, 5));
      } finally {
        setLoadingPreview(false);
      }
    })();
  }, [value.extraEmails, value.listIds, value.segments]);

  const addEmails = (raw) => {
    const emails = raw.split(/[\s,;\n]+/).map(s => s.trim()).filter(s => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s));
    if (emails.length === 0) return;
    const set = new Set([...(value.extraEmails || []).map(e => e.toLowerCase())]);
    const added = [];
    for (const e of emails) {
      if (!set.has(e.toLowerCase())) {
        set.add(e.toLowerCase());
        added.push(e);
      }
    }
    if (added.length > 0) {
      onChange({ ...value, extraEmails: [...value.extraEmails, ...added] });
    }
  };

  const removeEmail = (email) => {
    onChange({ ...value, extraEmails: value.extraEmails.filter(e => e !== email) });
  };

  const toggleList = (listId) => {
    const has = value.listIds.includes(listId);
    onChange({
      ...value,
      listIds: has ? value.listIds.filter(id => id !== listId) : [...value.listIds, listId]
    });
  };

  const toggleSegment = (segment, extra = {}) => {
    const key = JSON.stringify({ segment, ...extra });
    const existing = value.segments.findIndex(s => JSON.stringify(s) === key);
    if (existing >= 0) {
      onChange({ ...value, segments: value.segments.filter((_, i) => i !== existing) });
    } else {
      onChange({ ...value, segments: [...value.segments, { segment, ...extra }] });
    }
  };

  const segmentChecked = (segment, extra = {}) => {
    const key = JSON.stringify({ segment, ...extra });
    return value.segments.some(s => JSON.stringify(s) === key);
  };

  return (
    <div className="space-y-3">
      {/* Preview bar */}
      <div className="flex items-center justify-between px-3 py-2 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-md">
        <div className="flex items-center gap-2 text-sm">
          <FiUsers className="w-4 h-4 text-primary-600 dark:text-primary-400" />
          <span className="font-medium text-gray-900 dark:text-white">
            {loadingPreview ? 'Calculating…' : `${previewCount} recipient${previewCount === 1 ? '' : 's'}`}
          </span>
          {previewSample.length > 0 && !loadingPreview && (
            <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-md">
              · {previewSample.slice(0, 3).join(', ')}{previewCount > 3 ? ` +${previewCount - 3} more` : ''}
            </span>
          )}
        </div>
        <span className="text-[10px] text-gray-500 dark:text-gray-400">(unsubs excluded automatically)</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Ad-hoc emails */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-3">
          <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">
            <FiMail className="w-3.5 h-3.5" />
            Ad-hoc emails
          </label>
          <div className="flex flex-wrap gap-1 mb-2 min-h-[24px]">
            {value.extraEmails.map(email => (
              <span key={email} className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs text-gray-800 dark:text-gray-200">
                {email}
                <button
                  onClick={() => removeEmail(email)}
                  className="hover:text-red-500 transition-colors"
                  aria-label={`Remove ${email}`}
                >
                  <FiX className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          <input
            type="text"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
                e.preventDefault();
                addEmails(emailInput);
                setEmailInput('');
              }
            }}
            onBlur={() => { addEmails(emailInput); setEmailInput(''); }}
            placeholder="Type an email and press Enter"
            className="w-full px-3 py-1.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm text-gray-900 dark:text-white placeholder-gray-400"
          />
        </div>

        {/* Saved lists */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-3">
          <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">
            <FiList className="w-3.5 h-3.5" />
            Saved Lists
          </label>
          {lists.length === 0 ? (
            <p className="text-xs text-gray-500 dark:text-gray-400">No lists yet. Create one on the Lists tab.</p>
          ) : (
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {lists.map(l => (
                <label key={l.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={value.listIds.includes(l.id)}
                    onChange={() => toggleList(l.id)}
                    className="rounded text-primary-600 focus:ring-primary-500"
                  />
                  <span className="flex-1 text-sm text-gray-900 dark:text-white">{l.name}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{l.memberCount ?? 0}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Segments */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-3 md:col-span-2">
          <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">
            <FiUser className="w-3.5 h-3.5" />
            Segments
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {[
              { key: 'team_admins', label: 'All team admins' },
              { key: 'active_users', label: 'All active users' },
              { key: 'stalled_signups', label: 'All stalled signups' }
            ].map(s => (
              <label key={s.key} className="flex items-center gap-2 px-2 py-1.5 rounded border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={segmentChecked(s.key)}
                  onChange={() => toggleSegment(s.key)}
                  className="rounded text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-900 dark:text-white">{s.label}</span>
              </label>
            ))}
          </div>

          {teams.length > 0 && (
            <div className="mt-2">
              <label className="block text-[10px] font-medium text-gray-600 dark:text-gray-400 mb-1">Or pick a specific team</label>
              <select
                value=""
                onChange={(e) => { if (e.target.value) toggleSegment('team', { teamId: e.target.value }); e.target.value = ''; }}
                className="w-full px-3 py-1.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm text-gray-900 dark:text-white"
              >
                <option value="">— Add team —</option>
                {teams.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>

              {value.segments.filter(s => s.segment === 'team').length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {value.segments.filter(s => s.segment === 'team').map(s => {
                    const team = teams.find(t => t.id === s.teamId);
                    return (
                      <span key={s.teamId} className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded text-xs">
                        Team: {team?.name || s.teamId}
                        <button
                          onClick={() => toggleSegment('team', { teamId: s.teamId })}
                          className="hover:text-red-500 transition-colors"
                          aria-label={`Remove ${team?.name || 'team'}`}
                        >
                          <FiX className="w-3 h-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecipientPicker;
